import { isString } from "lodash";
import { enqueueSnackbar } from "notistack";
import { useState, useEffect, useCallback } from "react";

import { Stack } from "@mui/system";
import { LoadingButton } from "@mui/lab";
import { Card, Avatar, Tooltip, TextField, IconButton, Typography, CardContent, Autocomplete, ListItemText } from "@mui/material";

import { useBoolean } from "src/hooks/use-boolean";
import { useOrgTenant } from "src/hooks/auth/use-org-tenant";
import { useAuthContext } from "src/hooks/auth/use-auth-context";

import { useTranslate } from "src/locales";
import { useAppSelector } from "src/store/store";
import { User } from "src/services/organisation/organisation.types";
import { CompanyProfileType } from "src/services/connections/connections.types";
import { useGetCompanyByIdQuery, useUpdateCompanyAccountOwnerMutation } from "src/services/connections/connections.service";

import Iconify from "src/components/iconify";
import { ConfirmDialog } from "src/components/custom-dialog";
import TruncatedTextField from "src/components/truncate-text/truncated-text-field";
import AccountDisplay from "src/components/account-viewer/account-display";

type Props = {
  profileId: string;
}

const AccountOwnerCard = ({ profileId }: Props) => {

  const showEditUserDialog = useBoolean();

  const { t } = useTranslate();

  const tenantType = useOrgTenant();

  const authContext = useAuthContext();

  const { data: companyProfile } = useGetCompanyByIdQuery({ profile_id: profileId }, {
    skip: !profileId,
  });

  const users = useAppSelector((state) => state?.data?.users);

  const [updateAccountOwner, { isLoading: isUpdatingAccountOwner }] = useUpdateCompanyAccountOwnerMutation();

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [currentuser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    if (companyProfile?.account_owner) {
      if (users?.length) {
        const userAccount = users.find((user) => user.id === companyProfile.account_owner?.id);
        setSelectedUser(userAccount ?? null);
        setCurrentUser(userAccount ?? null);
      }
    }
    else {
      setSelectedUser(null);
      setCurrentUser(null);
    }
  }, [companyProfile?.account_owner, users]);

  const handleUpdateAccountOWner = useCallback(async () => {
    if (!companyProfile || !selectedUser) return;

    if (![CompanyProfileType.UNLINKED, CompanyProfileType.LINKED].includes(companyProfile.type)) {
      enqueueSnackbar(t('companies.profile.api.update.linked_profile_error'), { variant: 'error' });
      return;
    }

    try {

      await updateAccountOwner({
        profile_id: profileId,
        data: {
          account_id: selectedUser.id as string
        },
      }).unwrap();

      enqueueSnackbar(t('companies.profile.api.update_account_owner.success'), { variant: 'success' });
      showEditUserDialog.onFalse();
    } catch (error) {
      enqueueSnackbar(t('companies.profile.api.update_account_owner.default_error'), { variant: 'error' });
    }
  }, [companyProfile, selectedUser, showEditUserDialog, profileId, updateAccountOwner, t]);

  const handleRemoveAccountOwner = useCallback(async () => {
    if (!companyProfile) return;

    if (![CompanyProfileType.UNLINKED, CompanyProfileType.LINKED].includes(companyProfile.type)) {
      enqueueSnackbar(t('companies.profile.api.update.linked_profile_error'), { variant: 'error' });
      return;
    }

    try {
      await updateAccountOwner({
        profile_id: profileId,
        data: {
          account_id: null
        },
      }).unwrap();

      enqueueSnackbar(t('companies.profile.api.remove_account_owner.success'), { variant: 'success' });
      showEditUserDialog.onFalse();
    } catch (error) {
      enqueueSnackbar(t('companies.profile.api.remove_account_owner.default_error'), { variant: 'error' });
    }
  }, [companyProfile, showEditUserDialog, profileId, updateAccountOwner, t]);

  return (
    <>
      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} display="flex" alignItems="center">
              <Avatar
                alt={currentuser ? currentuser.first_name : undefined}
                src={currentuser ? currentuser?.profile_image?.public_path : undefined}
                variant="rounded"
                sx={{ width: 48, height: 48 }}
              />

              <Stack direction="row" display="flex" width="100%" justifyContent="space-between">
                <ListItemText
                  primary={t('companies.profile.account_owner.title')}
                  secondary={
                    currentuser ?
                      <AccountDisplay
                        accountId={currentuser.id as string}
                        textFieldProps={{
                          limit: 15,
                          text: `${currentuser.first_name} ${currentuser.last_name}`,
                          mode: 'characters',
                          showMoreEnabled: false,
                          variant: 'body2'
                        }}
                      />
                      :
                      <Typography variant="body2" color="text.secondary">
                        {t('companies.profile.primary_contact.no_primary_contact')}
                      </Typography>
                  }
                />

                <Stack direction="row" spacing={1} display="flex" alignItems="center">

                  {
                    companyProfile?.account_owner && (
                      <Tooltip title={t('companies.profile.account_owner.remove_tooltip')} arrow placement="top">
                        <IconButton onClick={handleRemoveAccountOwner} color="error" sx={{ p: .3 }}>
                          <Iconify icon="mingcute:user-remove-fill" />
                        </IconButton>
                      </Tooltip>
                    )
                  }

                  <Tooltip title={t('companies.profile.account_owner.update_tooltip')} arrow placement="top">
                    <IconButton onClick={showEditUserDialog.onTrue} sx={{ p: .3 }}>
                      <Iconify icon="solar:pen-bold" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {
        (companyProfile && users?.length !== undefined) && (
          <ConfirmDialog
            open={showEditUserDialog.value}
            title={t('companies.profile.account_owner.edit')}
            onClose={showEditUserDialog.onFalse}
            content={
              <Autocomplete
                sx={{
                  paddingTop: 2,
                }}
                multiple={false}
                options={users as User[]}
                value={selectedUser as User}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(
                      'admin-organization.department.department_add_user.user_autocomplete.label'
                    )}
                    placeholder={t(
                      'admin-organization.department.department_add_user.user_autocomplete.placeholder'
                    )}
                  />
                )}
                onChange={(_e, value: User | null) => setSelectedUser(value)}
                renderOption={({ key, ...rest }, option: User) => (
                  <li key={key} {...rest}>
                    <Stack direction="column">
                      <Typography variant="body1">
                        {`${option.first_name} ${option.last_name}`}
                      </Typography>
                    </Stack>
                  </li>
                )}
                getOptionLabel={(option) =>
                  !isString(option) ? `${option.first_name} ${option.last_name}` : 'Assign to me'
                }
                isOptionEqualToValue={(option, value) =>
                  option && (option as User).id === (value as User).id
                }
                getOptionKey={(option) => (option as User).id as string}
              />
            }
            action={
              <LoadingButton
                variant="contained"
                disabled={!selectedUser}
                onClick={handleUpdateAccountOWner}
                loading={isUpdatingAccountOwner}>
                {t('companies.profile.account_owner.assign')}
              </LoadingButton>
            }
          />
        )
      }

    </>
  )

}

export default AccountOwnerCard; 